import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { toastApiError } from '@/queries/common';
import { DataUploadType } from '@/common/data-upload';
import { addDataUpload } from '@/queries/data-uploads/add-data-upload';
import {
	PRESIGNED_UPLOAD_SCOPE,
	uploadFileWithPresignedUrl,
} from '@/queries/uploads/presigned-upload';

export type UploadTarget = {
	damageId: string;
	inspectionId?: string;
};

export function useBulkDamageUpload() {
	const [isUploading, setIsUploading] = useState(false);
	const queryClient = useQueryClient();

	const uploadMutation = useMutation({
		mutationFn: async ({
			file,
			targets,
			assetId,
		}: {
			file: File;
			targets: UploadTarget[];
			assetId: string;
		}) => {
			setIsUploading(true);
			try {
				const { key } = await uploadFileWithPresignedUrl(file, {
					//* Use the ASSET scope for bulk uploads, as we are uploading files related to an asset. And the individual damage associations will be handled in the database records.
					scope: PRESIGNED_UPLOAD_SCOPE.ASSET,
					assetId,
				});

				// Create a database record for EVERY selected damage — store S3 key as fileUrl.
				// Read access is via GET /data-uploads/:id/presigned-url.
				await Promise.all(
					targets.map((target) =>
						addDataUpload({
							name: file.name,
							assetId: assetId,
							fileType: file.type,
							fileSizeInKB: (file.size || 0) / 1024,
							fileUrl: key,
							uploadType: DataUploadType.FILE,
							damageId: target.damageId,
							inspectionId: target.inspectionId,
						})
					)
				);

				return true;
			} catch (error) {
				console.error('Bulk upload failed', error);
				throw error;
			} finally {
				setIsUploading(false);
			}
		},
		onSuccess: async () => {
			toast.success(t('damages.toast.bulkUploadSuccess'));

			// Refresh File List (DataUploadListContainer)
			await queryClient.invalidateQueries({ queryKey: ['data-uploads'] });

			// Refresh Polygon List / 3D Map (DamagesLoader)
			await queryClient.invalidateQueries({ queryKey: ['damage-polygons'] });

			// Refresh Damage List
			await queryClient.invalidateQueries({ queryKey: ['damages'] });
		},
		onError: (error) => toastApiError(error),
	});

	return {
		upload: uploadMutation.mutateAsync,
		isLoading: isUploading,
	};
}
